/**
 * ─────────────────────────────────────────────
 *   Client-Side Cache Utility
 *   Stale-While-Revalidate pattern with TTL
 * ─────────────────────────────────────────────
 */

const CACHE_VERSION = 'v99_clean_v1';
const CACHE_PREFIX = 'tma_cache_';

// ─── TTL Constants (ms) ───
export const TTL = {
    USER:        10 * 60 * 1000,  // 10 min — user profile
    SKINS:       30 * 60 * 1000,  // 30 min — skins/brands (rarely change)
    DISCOVERY:    5 * 60 * 1000,  //  5 min — home discovery strips
    HOME:         5 * 60 * 1000,  //  5 min — home sections
    LISTINGS:     3 * 60 * 1000,  //  3 min — market listings
    CARDS:        5 * 60 * 1000,  //  5 min — user cards
    AUCTIONS:    30 * 1000,       // 30 sec — active auctions (fast-moving)
    ACTIVE_USERS: 2 * 60 * 1000, //  2 min — live user count
} as const;

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    version: string;
}

/**
 * Read data from localStorage cache.
 * Returns null if: not found, expired, or version mismatch.
 */
export function getCached<T>(key: string, ttlMs: number): T | null {
    try {
        const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`);
        if (!raw) return null;

        const entry: CacheEntry<T> = JSON.parse(raw);

        if (entry.version !== CACHE_VERSION) {
            localStorage.removeItem(`${CACHE_PREFIX}${key}`);
            return null;
        }

        if (Date.now() - entry.timestamp > ttlMs) {
            return null; // stale — let caller re-fetch
        }

        return entry.data;
    } catch {
        return null;
    }
}

/**
 * Read data from localStorage cache IGNORING TTL.
 * Returns null only if: not found, or version mismatch.
 * This is useful for 'Initial UI State' before revalidation.
 */
export function getAny<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`);
        if (!raw) return null;

        const entry: CacheEntry<T> = JSON.parse(raw);

        if (entry.version !== CACHE_VERSION) {
            localStorage.removeItem(`${CACHE_PREFIX}${key}`);
            return null;
        }

        return entry.data;
    } catch {
        return null;
    }
}

/**
 * Write data to localStorage cache with current timestamp.
 */
export function setCached<T>(key: string, data: T): void {
    try {
        const entry: CacheEntry<T> = {
            data,
            timestamp: Date.now(),
            version: CACHE_VERSION,
        };
        localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
    } catch (e) {
        // localStorage might be full — silently fail
        console.warn('[Cache] Write failed:', e);
    }
}

/**
 * Get timestamp of when data was last cached.
 * Returns 0 if not cached.
 */
export function getCacheAge(key: string): number {
    try {
        const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`);
        if (!raw) return 0;
        const entry = JSON.parse(raw);
        return entry.timestamp || 0;
    } catch {
        return 0;
    }
}

/**
 * Check if cached data is still fresh.
 */
export function isCacheFresh(key: string, ttlMs: number): boolean {
    const age = getCacheAge(key);
    if (!age) return false;
    return Date.now() - age < ttlMs;
}

/**
 * Invalidate a specific cache entry.
 */
export function invalidateCache(key: string): void {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
}

/**
 * Invalidate ALL app cache entries (e.g. on logout).
 */
export function invalidateAllCache(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
}
